import React from 'react'
import { AlignJustify } from 'lucide-react'
import { v4 as uuid } from 'uuid'
import WithTooltip from 'components/shared/WithTooltip'
import usePanelContext from 'refactor/hooks/usePanelContext'
import useStore from 'state/knovStore'
import { isMobile } from 'lib/device'
import {
    getCenterIndex,
    getLeftIndex,
    getSwiperRef,
    slideNext,
    slidePrev,
    slideTo,
    SWIPE_SPEED_FAST,
} from 'state/imperativeApis/swiperApi'
import { userHasFeature } from 'refactor/hooks/useUserHasFeature'
import { primeQuestForInstantOpen } from 'refactor/panels/primeChildQuestForOpen'
import useOpenRightPrefetch from 'refactor/hooks/useOpenRightPrefetch'
import {
    planOriginInsertPresentation,
    planOriginNavigation,
    planOriginRetargetSlide,
} from 'lib/quoteMain'
import styles from './main-thread-origin-marker.module.scss'

interface MainThreadOriginMarkerProps {
    answer: { id: string; quest_id?: string | null }
}

// A branch's main-thread guest wears this in the gutter left of its avatar,
// flush with the panel edge: the row was said in the main thread, and pressing
// the marker opens the main thread AT that reply, to the left — where the
// parent already sits in the panel row. The quote line on the same row is a
// regular quote of the branch head and keeps scrolling up to it.
const MainThreadOriginMarker: React.FC<MainThreadOriginMarkerProps> = ({ answer }) => {
    const { panelId } = usePanelContext()
    const prefetchHandlers = useOpenRightPrefetch(answer.quest_id, answer.id)

    const onClick = (ev: React.MouseEvent) => {
        ev.preventDefault()
        ev.stopPropagation()
        const panels = useStore.getState().panels
        const threePanel = !isMobile && useStore.getState().numPanels !== 1
        const left = panels.getPanelToLeft(panelId)
        const plan = planOriginNavigation({
            panelToLeft: left,
            originQuestId: answer.quest_id,
            answerId: answer.id,
        })
        if (!plan) return
        // Overlaps the quest fetch with the panel work (a no-op unless
        // instant_reply_nav is on), the way openRight and openCenter do.
        primeQuestForInstantOpen(plan.questId, plan.answerId)

        if (plan.kind === 'retarget-left') {
            // The same pair a reused panel gets from openRight: the filter earns the
            // deep-link support, the explicit request guarantees the reveal fires
            // even though the panel's route effect keys on an unchanged instance.
            panels.editPanel(plan.panelId, {
                filter: { ...(left?.filter || {}), questId: plan.questId, answerId: plan.answerId },
            })
            void left?.requestAnswerReveal?.(plan.answerId, {
                questId: plan.questId,
                highlight: true,
            })
            // The reveal waits for its panel to be on screen; bring it there.
            const slide = planOriginRetargetSlide({
                threePanel,
                targetIndex: panels.getIndexById(plan.panelId),
                activeIndex: getLeftIndex(),
            })
            if (slide?.kind === 'slide-to') slideTo(slide.index, SWIPE_SPEED_FAST)
            else if (slide?.kind === 'slide-prev') slidePrev(1, SWIPE_SPEED_FAST)
            return
        }

        const presentation = planOriginInsertPresentation({
            threePanel,
            anchorIndex: panels.getIndexById(panelId),
            centerIndex: getCenterIndex(),
        })
        // A panel the reader has not seen yet places its target pre-paint under
        // instant_reply_nav, as openRight's fresh panels do; flag-off the
        // route-owned reveal scrolls to it after mount.
        const placementIntent = userHasFeature('instant_reply_nav')
            ? { questId: plan.questId, answerId: plan.answerId }
            : undefined
        panels.insertPanelLeft(
            panelId,
            {
                panelId: uuid(),
                filter: { questId: plan.questId, answerId: plan.answerId },
                empty: false,
                ...(placementIntent ? { pendingOpenPlacement: placementIntent } : {}),
            },
            { animate: presentation.animate },
        )
        if (presentation.slideNext) {
            // Let Swiper register the new virtual slide before moving onto it.
            requestAnimationFrame(() => {
                try {
                    getSwiperRef()?.current?.virtual?.update?.(true)
                } catch {}
                slideNext(1, SWIPE_SPEED_FAST)
            })
        }
    }

    return (
        <WithTooltip asChild tip="Open in the main thread">
            <button
                type="button"
                className={styles.marker}
                aria-label="Open in the main thread"
                data-testid="main-thread-origin-marker"
                onClick={onClick}
                onMouseEnter={prefetchHandlers.onMouseEnter}
                onMouseLeave={prefetchHandlers.onMouseLeave}
                onFocus={prefetchHandlers.onFocus}
                onBlur={prefetchHandlers.onBlur}
                onPointerDown={prefetchHandlers.onPointerDown}
            >
                <AlignJustify strokeWidth={2.5} aria-hidden="true" />
            </button>
        </WithTooltip>
    )
}

export default MainThreadOriginMarker
